import React, { useState, useMemo } from 'react'
import CommonCard from "../../../../global/card"
import { useLocation } from "react-router-dom"
import SOURCES from '../../../../../utils/constants'
import { useGenericQuery } from '../../../../../queries/use-generic-query'
import { QUERY_KEYS } from '../../../../../lib/query-constants'
import CommonSkeletonLoader from '../../../../global/loading'
import useDebounce from '../../../../../utils/customHooks'
import EnhancedEmailLogList from './enhancedEmailLogList'
import CommonPagination from '../../../../global/pagination'
import EnhancedEmailLogCardView from './enhancedEmailLogCardView'
import CommonCardView from '../../../../global/cardView'
import { EyeOutlined } from "@ant-design/icons";
import EnhancedEmailLogDetails from './enhancedEmailLogDetails'
import CommonDrawer from '../../../../global/drawer'

const EnhancedEmailLogs = () => {
    const [searchEmailLogs, setSearchEmailLogs] = useState("")
    const [emailLogDetail, setEmailLogDetail] = useState(null);
    const [showDetail, setShowDetail] = useState(false);

    const [emailLogsViewType, setEmailLogsViewType] = useState(() => {
        return localStorage.getItem("emailLogsViewType") || "GRID";
    });

    const debouncedSearchTerm = useDebounce(searchEmailLogs, 500);

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const pageFromQuery = parseInt(searchParams.get("page") || 1, 10);
    const limitFromQuery = parseInt(
        searchParams.get("limit") || SOURCES.DEFAULT_RECORDS_LISTING,
        24
    );
    const offsetFromQuery = parseInt(searchParams.get("offset") || "0", 10);
    const [sort, setSort] = useState({ sortType: "", sortOrder: 1 });

    const [pagination, setPagination] = useState({
        offset: offsetFromQuery,
        limit: limitFromQuery,
        page: pageFromQuery,
    });

    const keyParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy: sort.sortType,
        sortOrder: sort.sortOrder,
    }), [pagination.offset, pagination.limit, sort.sortType, sort.sortOrder]);

    const { data, isLoading } = useGenericQuery({
        endpoint: "admin/email/log",
        queryKey: QUERY_KEYS.emailLogs.lists({ ...keyParams }),
        queryParams: { ...keyParams },
        enabled: !debouncedSearchTerm,
        onSuccess: () => { },
        onError: () => { }
    })

    const { data: searchData, isInitialLoading: searchLoading } = useGenericQuery({
        endpoint: "admin/email/log/search",
        queryKey: QUERY_KEYS.emailLogs.lists({ q: debouncedSearchTerm }),
        queryParams: { q: debouncedSearchTerm },
        onSuccess: () => { },
        onError: () => { },
        enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= 2,
    })

    const isLoadingList = isLoading || searchLoading;

    const finalizedEmailLogs = useMemo(() => {
        if (debouncedSearchTerm && searchData) {
            return searchData;
        }
        return data;
    }, [data, searchData, debouncedSearchTerm]);

    const viewDetail = (item) => {
        setEmailLogDetail(item);
        setShowDetail(true);
    };

    const actions = [
        {
            label: "View Detail",
            icon: <EyeOutlined />,
            onClick: (record) => viewDetail(record),
            className: "!text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white"
        }
    ];

    const finalizedData = useMemo(() => {
        if (!finalizedEmailLogs || !finalizedEmailLogs.logs) return [];
        return finalizedEmailLogs.logs.map((item, idx) => ({
            ...item,
            key: item._id || idx,
            id: item._id || idx,
        }));
    }, [finalizedEmailLogs]);


    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search Email Logs",
                searchInput: searchEmailLogs,
                setSearchInput: setSearchEmailLogs
            }}
            isLoading={{
                show: isLoadingList,
                loaderCmp: <CommonSkeletonLoader type={emailLogsViewType === "GRID" ? "card" : "table"} />
            }}
            isListGridView={{
                show: true,
                setViewType: setEmailLogsViewType,
                viewType: emailLogsViewType,
                viewFor: "emailLogsViewType"
            }}
        >
            {emailLogsViewType === "LIST" ? (
                <EnhancedEmailLogList
                    emailLogs={finalizedEmailLogs}
                    sort={sort}
                    setSort={setSort}
                    isLoading={isLoadingList}
                    viewDetail={viewDetail}
                />
            ) : (
                <CommonCardView>
                    {finalizedData.map((record) => (
                        <EnhancedEmailLogCardView
                            key={record.id}
                            record={record}
                            actions={actions}
                            onCardClick={(record) => viewDetail(record)}
                        />
                    ))}
                </CommonCardView>
            )}

            {
                !debouncedSearchTerm &&
                finalizedEmailLogs?.totalRecords > SOURCES.DEFAULT_RECORDS_LISTING && (
                    <CommonPagination
                        currentPage={pagination.page}
                        defaultLimit={pagination.limit}
                        totalRecords={finalizedEmailLogs?.totalRecords}
                        setCurrentPage={setPagination}
                        activePageRecords={finalizedEmailLogs?.logs?.length || 0}
                        paginationFor="Email Logs"
                        className="mt-4"
                    />
                )
            }
            {
                showDetail && (
                    <CommonDrawer
                        open={showDetail}
                        setOpen={() => setShowDetail(false)}
                        title="Email Log Details"
                        width={"1000px"}
                        children={
                            <EnhancedEmailLogDetails
                                emailLogDetail={emailLogDetail}
                            />
                        }
                    />
                )
            }
        </CommonCard>
    )
}

export default EnhancedEmailLogs